import { loginScopes } from "../auth/msalConfig";

const GRAPH_BASE      = "https://graph.microsoft.com/v1.0";
const CONST_SP_SITE   = "alliedconstructionmanagemen.sharepoint.com:/sites/construction";
const CONST_BASE_PATH = "CleanSpace";
// The same site as a browser URL, for showing and linking a job folder
// (src/pages/turnover/folderPath.js) — keep in step with CONST_SP_SITE.
export const CONSTRUCTION_SITE_URL = "https://alliedconstructionmanagemen.sharepoint.com/sites/construction";

// A job's folder lives under its PRIME contracting entity's top-level folder
// (Allied jobs under Allied, CleanShield under CleanShield; TECHspace jobs live
// under CleanSpace). Used by folder creation (Turnovers.jsx), every upload
// and the header's folder strip, so all three agree. Before 2026-09-28 uploads
// always went under CleanSpace, whoever held the prime contract.
export const ENTITY_BASE_PATH = {
  CleanSpace:  "CleanSpace",
  CleanShield: "CleanShield",
  Allied:      "Allied",
  TECHspace:   "CleanSpace",
};
export const entityBasePath = (entity) => ENTITY_BASE_PATH[entity] || CONST_BASE_PATH;
const EST_SP_SITE     = "alliedconstructionmanagemen.sharepoint.com:/sites/Estimating";

export async function getGraphToken(instance, accounts) {
  const res = await instance.acquireTokenSilent({ scopes: loginScopes, account: accounts[0] });
  return res.accessToken;
}

export async function graphGet(token, url) {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`Graph ${res.status}`);
  return res.json();
}

export async function getEstimatingDrive(token) {
  const site   = await fetch(`${GRAPH_BASE}/sites/${EST_SP_SITE}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
  const drives = await fetch(`${GRAPH_BASE}/sites/${site.id}/drives`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
  const drive  = drives.value.find(d => d.name === "Documents" || d.name === "Shared Documents") || drives.value[0];
  return { siteId: site.id, driveId: drive.id };
}

export async function uploadToEstimatingTurnover(token, driveId, turnoverFolderPath, file) {
  const uploadPath = `${turnoverFolderPath}/${file.name}`;
  const uploadUrl  = `${GRAPH_BASE}/drives/${driveId}/root:/${uploadPath}:/content`;
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!res.ok) throw new Error(`Estimating SP upload failed (${res.status})`);
  const data = await res.json();
  return { webUrl: data.webUrl, name: data.name };
}

export async function getConstructionDrive(token) {
  const site   = await graphGet(token, `${GRAPH_BASE}/sites/${CONST_SP_SITE}`);
  const drives = await graphGet(token, `${GRAPH_BASE}/sites/${site.id}/drives`);
  const drive  = drives.value.find(d => d.name === "Documents" || d.name === "Shared Documents") || drives.value[0];
  return { siteId: site.id, driveId: drive.id };
}

// Upload a file to: CleanSpace/{year} Jobs/{jobFolder}/Turnover/{##} - {sectionName}/{filename}
// Pass sectionNumber=0 and sectionName="" to place the file directly under Turnover/ (no subfolder).
// folderItemId: stable Graph drive-item ID for the job folder. When present, uploads anchor
// to it instead of rebuilding the path from year/jobFolder — stays valid even if the folder
// is later renamed in SharePoint. NULL for turnovers created before this column existed.
// basePath: the prime contracting entity's folder (entityBasePath).
export async function uploadToSharePoint(token, siteId, driveId, jobFolder, year, sectionName, file, sectionNumber = 0, folderItemId = null, basePath = CONST_BASE_PATH) {
  const safeSectionName = sectionName.replace(/[/\\:*?"<>|]/g, "-");
  const numPrefix       = sectionNumber > 0 ? String(sectionNumber).padStart(2, "0") + " - " : "";
  const sectionPath     = (sectionNumber > 0 || safeSectionName) ? `${numPrefix}${safeSectionName}/` : "";
  const relativePath    = `Turnover/${sectionPath}${file.name}`;
  const uploadUrl       = folderItemId
    ? `${GRAPH_BASE}/sites/${siteId}/drives/${driveId}/items/${folderItemId}:/${relativePath}:/content`
    : `${GRAPH_BASE}/sites/${siteId}/drives/${driveId}/root:/${basePath}/${year} Jobs/${jobFolder}/${relativePath}:/content`;

  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": file.type || "application/octet-stream",
    },
    body: file,
  });

  if (!res.ok) {
    const errText = await res.text();
    throw new Error(`Upload failed (${res.status}): ${errText}`);
  }

  const data = await res.json();
  return { webUrl: data.webUrl, name: data.name };
}

// ── Change a turnover's job folder (2026-09-28) ─────────────────────────────
// The folders directly inside <entity>/<year> Jobs, for picking an existing
// job folder. [] when that year folder doesn't exist.
export async function listJobFolders(token, driveId, entityFolder, year) {
  const path = encodeURI(`${entityFolder}/${year} Jobs`);
  const res = await fetch(`${GRAPH_BASE}/drives/${driveId}/root:/${path}:/children?$select=id,name,webUrl,folder&$top=999`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 404) return [];
  if (!res.ok) throw new Error(`Couldn't list ${entityFolder}/${year} Jobs (${res.status})`);
  return ((await res.json()).value || []).filter(i => i.folder);
}

// A pasted SharePoint folder link → { id, name, webUrl } — only when it is a
// folder in the given (Construction) library.
export async function resolveFolderLink(token, driveId, url, shareId) {
  const res = await fetch(`${GRAPH_BASE}/shares/${shareId}/driveItem?$select=id,name,webUrl,folder,parentReference`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`That link couldn't be opened (${res.status}).`);
  const item = await res.json();
  if (!item.folder) throw new Error("That link is a file, not a folder.");
  if (item.parentReference?.driveId !== driveId) throw new Error("That folder isn't in the Construction documents library.");
  return { id: item.id, name: item.name, webUrl: item.webUrl };
}

// Make sure <folder>/Turnover and its numbered section subfolders exist.
// Existing folders are left as they are (409 = already there). Returns the
// names it created.
export async function ensureTurnoverSubfolders(token, driveId, folderItemId, sectionFolderNames = []) {
  const h = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const create = async (parentId, name) => {
    const res = await fetch(`${GRAPH_BASE}/drives/${driveId}/items/${parentId}/children`, {
      method: "POST", headers: h, body: JSON.stringify({ name, folder: {}, "@microsoft.graph.conflictBehavior": "fail" }),
    });
    if (res.ok) return { created: true, id: (await res.json()).id };
    if (res.status === 409) return { created: false };
    throw new Error(`Couldn't create "${name}" (${res.status})`);
  };
  const created = [];
  const t = await create(folderItemId, "Turnover");
  if (t.created) created.push("Turnover");
  const turnoverId = t.id || (await fetch(`${GRAPH_BASE}/drives/${driveId}/items/${folderItemId}:/Turnover?$select=id`, { headers: h }).then(r => r.json())).id;
  for (const name of sectionFolderNames) {
    if ((await create(turnoverId, name)).created) created.push(name);
  }
  return created;
}

// Every file under a job folder, however deep (Billing, Contract, Turnover/…),
// with its content fingerprint — for spotting the same file saved twice in
// different folders (src/pages/turnover/duplicateFiles.js). Walks the tree
// breadth-first, following @odata.nextLink; stops at `maxItems` and says so.
export async function listFolderFilesDeep(token, driveId, folderId, { maxItems = 4000 } = {}) {
  const headers = { Authorization: `Bearer ${token}` };
  const files = [];
  const queue = [{ id: folderId, path: "" }];
  let seen = 0, truncated = false;
  while (queue.length) {
    const { id, path } = queue.shift();
    let url = `${GRAPH_BASE}/drives/${driveId}/items/${id}/children?$select=id,name,webUrl,size,file,folder&$top=999`;
    while (url) {
      const res = await fetch(url, { headers });
      if (!res.ok) throw new Error(`Couldn't read the job folder (${res.status})`);
      const page = await res.json();
      for (const item of page.value || []) {
        if (++seen > maxItems) { truncated = true; break; }
        const itemPath = path ? `${path}/${item.name}` : item.name;
        if (item.folder) queue.push({ id: item.id, path: itemPath });
        else if (item.file) files.push({ id: item.id, name: item.name, webUrl: item.webUrl, size: item.size, hash: item.file.hashes?.quickXorHash || null, path: itemPath });
      }
      url = truncated ? null : page["@odata.nextLink"];
    }
    if (truncated) break;
  }
  return { files, truncated };
}
